import { lazy, Suspense, type ReactNode } from 'react';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import { Button, buttonVariants } from '@worldcoo/ui/components/button';
import { isNotFound, Link, useRouteContext } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { CampaignStatistics } from './statistics-campaign';
import { DivWithPrimaryColorVariable } from './div-with-primary-color-variable';
import { cn, heroImageSize } from '@worldcoo/ui/lib/utils';
import { Image } from '../../-components/image';
import { usePortalCampaignQuery } from '@/lib/data-hooks';
import { useCampaignDisplayContext } from './campaign-display-context';
import { DonationsIcon } from './assets/donations-icon';
import { CollectedIcon } from './assets/collected-icon';
import { formatNumber } from '@worldcoo/ui/lib/utils';
import { NotTranslatedInfo } from './not-translated-info';
import { stripHtml } from '@worldcoo/shared/utils';

const VideoEmbed = lazy(() => import('./video-embed').then((m) => ({ default: m.VideoEmbed })));

export function CampaignHero(props: { campaignId: string }) {
	const { t } = useLingui();
	const displayData = useCampaignDisplayContext();
	if (displayData) {
		const { campaign, aggregates, onDonate } = displayData;
		return (
			<CampaignHeroContent
				textsNotFound={campaign.textsNotFound}
				corporateTitle={campaign.texts.corporateTitle}
				resumeShort={campaign.texts.resumeShort}
				finished={campaign.finished}
				statistics={
					aggregates && (
						<StaticCampaignStatistics
							aggregates={aggregates}
							locale={displayData.locale}
							hideCollected={displayData.metadata.hideCollected}
						/>
					)
				}
				donateAction={
					campaign.finished ? (
						<Button size="2xl" className="hidden sm:w-fit lg:flex" disabled>
							{t`Campaign funded`}
						</Button>
					) : (
						<Button size="2xl" className="hidden sm:w-fit lg:flex" onClick={onDonate}>
							{t`Make donation`}
						</Button>
					)
				}
				media={
					campaign.heroVideoUrl ? (
						<Suspense>
							<VideoEmbed url={campaign.heroVideoUrl} title={campaign.heroImageAlt} />
						</Suspense>
					) : campaign.media?.location ? (
						<img
							src={campaign.media.location}
							className={cn('w-full rounded-2xl object-cover', heroImageSize.className)}
							alt={campaign.heroImageAlt ?? 'Campaign hero image'}
						/>
					) : null
				}
			/>
		);
	}
	return (
		<Suspense
			fallback={
				<div className="flex flex-col lg:flex-row">
					<section className="flex w-full flex-col gap-6 px-6 pb-10 lg:w-1/2">
						<Skeleton className="bg-secondary h-9 w-32" />

						<div className="flex flex-col gap-1">
							<Skeleton className="bg-secondary h-6 w-lg" />
							<Skeleton className="bg-secondary h-6 w-md" />
							<Skeleton className="bg-secondary h-6 w-lg" />
							<br />
							<Skeleton className="bg-secondary h-6 w-lg" />
							<Skeleton className="bg-secondary h-6 w-md" />
							<br />
							<Skeleton className="bg-secondary h-6 w-lg" />
							<Skeleton className="bg-secondary h-6 w-md" />
							<Skeleton className="bg-secondary h-6 w-sm" />
						</div>

						<Skeleton className="bg-secondary h-14 w-48" />
					</section>

					<div className="w-full p-10 pb-14 lg:w-1/2">
						<Skeleton className={cn('bg-secondary h-full w-full rounded-2xl', heroImageSize.className)} />
					</div>
				</div>
			}
		>
			<SuspensedCampaignHero campaignId={props.campaignId} />
		</Suspense>
	);
}

function SuspensedCampaignHero(props: { campaignId: string }) {
	const { portalId } = useRouteContext({ from: '__root__' });
	const { t } = useLingui();
	const campaign = usePortalCampaignQuery(props.campaignId);

	if (!portalId || isNotFound(campaign)) return null;

	return (
		<CampaignHeroContent
			textsNotFound={campaign.data.textsNotFound}
			corporateTitle={campaign.data.texts.corporateTitle}
			resumeShort={campaign.data.texts.resumeShort}
			finished={campaign.data.finished}
			statistics={<CampaignStatistics campaignId={props.campaignId} />}
			donateAction={
				campaign.data.finished || campaign.data.disabled ? (
					<Button size="2xl" className="hidden sm:w-fit lg:flex" disabled>
						{t`Campaign funded`}
					</Button>
				) : (
					<Link
						to="/$portalId/$campaignId/donate"
						params={{ portalId, campaignId: campaign.data.id }}
						search={(prev) => prev}
						className={cn(buttonVariants({ size: '2xl' }), 'hidden sm:w-fit lg:flex')}
					>
						{t`Make donation`}
					</Link>
				)
			}
			media={
				campaign.data.heroVideoUrl ? (
					<Suspense>
						<VideoEmbed url={campaign.data.heroVideoUrl} title={campaign.data.heroVideoAlt} />
					</Suspense>
				) : (
					<Image
						src={campaign.data.media?.location}
						height={heroImageSize.height}
						aspectRatio={heroImageSize.width / heroImageSize.height}
						className={cn('w-full rounded-2xl object-cover transition-all', heroImageSize.className)}
						alt={campaign.data.heroImageAlt ?? 'Campaign hero image'}
						priority
					/>
				)
			}
		/>
	);
}

function CampaignHeroContent(props: {
	textsNotFound: boolean;
	corporateTitle?: string;
	resumeShort?: string;
	finished: boolean;
	statistics: ReactNode;
	donateAction: ReactNode;
	media: ReactNode;
}) {
	return (
		<DivWithPrimaryColorVariable className="bg-background">
			{props.textsNotFound && <NotTranslatedInfo className="mx-6 mb-6" />}

			<div className="flex flex-col lg:flex-row">
				<section className="flex w-full flex-col gap-6 px-6 pb-10 lg:w-1/2">
					{props.statistics}

					{props.corporateTitle && (
						// TODO: migrate data to use text without HTML and remove this stripHtml usage
						<h1 className="text-4xl font-medium">{stripHtml(props.corporateTitle)}</h1>
					)}

					{props.resumeShort && (
						<div
							className="unreset-tailwind"
							dangerouslySetInnerHTML={{
								__html: props.resumeShort,
							}}
						/>
					)}

					{props.donateAction}
				</section>

				<div className="w-full px-10 pb-14 lg:w-1/2">{props.media}</div>
			</div>
		</DivWithPrimaryColorVariable>
	);
}

function StaticCampaignStatistics(props: {
	aggregates: { donors: number; collected: number; funding?: number };
	locale: string;
	hideCollected: boolean;
}) {
	const { t } = useLingui();
	return (
		<div className={cn('bg-background flex gap-16', props.hideCollected && 'justify-center')}>
			<div className="flex flex-col font-medium">
				<DonationsIcon className="text-primary mb-2" />
				<div className="text-base whitespace-nowrap xl:text-2xl">
					{formatNumber(props.locale, props.aggregates.donors, 0)}
				</div>
				<span className="text-primary text-xs font-semibold text-nowrap lg:text-base">{t`Donations`}</span>
			</div>
			{!props.hideCollected && (
				<div className="flex flex-col font-medium">
					<CollectedIcon className="text-primary mb-2" />
					<div className="text-base whitespace-nowrap xl:text-2xl">
						{formatNumber(props.locale, props.aggregates.collected, 2)}€
					</div>
					<span className="text-primary text-xs font-semibold text-nowrap lg:text-base">
						{props.aggregates.funding
							? (() => {
									const goal = formatNumber(props.locale, props.aggregates.funding, 0);
									return t`Goal of ${goal}` + ' €';
								})()
							: t`Collected`}
					</span>
				</div>
			)}
		</div>
	);
}
